import {
  ChartColumnIncreasing,
  CircleDollarSign,
  Factory,
  LayoutGrid,
  Settings,
  ShoppingCart,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { findActiveModule, type NavItem } from './navigation';

export type WorkspaceId =
  'all' | 'operacional' | 'financeiro' | 'rh' | 'comercial' | 'compras' | 'administrativo';

export interface Workspace {
  id: WorkspaceId;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Rotas (`NavItem.to`) dos módulos da área, na ordem do menu. `null` = todos os módulos. */
  modules: string[] | null;
}

/** Presentes em todas as áreas: o painel inicial e os itens fixos na base da sidebar. */
const COMMON_MODULES = ['/dashboard'];

/**
 * Áreas de trabalho do administrador: recortes da sidebar por função. Um módulo pode estar em
 * mais de uma área; a busca global continua alcançando tudo.
 */
export const WORKSPACES: Workspace[] = [
  {
    id: 'operacional',
    label: 'Operacional',
    description: 'Produção, estoque, produtos e qualidade',
    icon: Factory,
    modules: [
      '/produtos',
      '/estoque',
      '/producao',
      '/patrimonio',
      '/manutencoes',
      '/horas-maquina',
      '/mtr',
      '/logistica',
      '/alertas',
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    description: 'Contas a pagar e receber, fluxo de caixa',
    icon: CircleDollarSign,
    modules: ['/financeiro', '/notas-fiscais', '/horas-maquina', '/relatorios'],
  },
  {
    id: 'rh',
    label: 'RH',
    description: 'Pessoas, folha de pagamento e benefícios',
    icon: UsersRound,
    modules: ['/rh', '/documentos', '/relatorios'],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    description: 'Vendas, clientes e propostas',
    icon: ChartColumnIncreasing,
    modules: ['/clientes', '/vendas', '/produtos', '/logistica', '/site', '/relatorios'],
  },
  {
    id: 'compras',
    label: 'Compras',
    description: 'Fornecedores e pedidos de compra',
    icon: ShoppingCart,
    modules: ['/fornecedores', '/compras', '/estoque', '/notas-fiscais'],
  },
  {
    id: 'administrativo',
    label: 'Administrativo',
    description: 'Processos, documentos e configurações',
    icon: Settings,
    modules: ['/documentos', '/notas-fiscais', '/alertas', '/site', '/relatorios'],
  },
  {
    id: 'all',
    label: 'Todos os módulos',
    description: 'Menu completo, sem recorte por área',
    icon: LayoutGrid,
    modules: null,
  },
];

export const DEFAULT_WORKSPACE: WorkspaceId = 'all';

const ALL_MODULES = WORKSPACES.find((w) => w.id === DEFAULT_WORKSPACE) as Workspace;

/** Área pelo id; valores desconhecidos (ex.: gravados por uma versão antiga) caem em "Todos". */
export function getWorkspace(id: WorkspaceId): Workspace {
  return WORKSPACES.find((w) => w.id === id) ?? ALL_MODULES;
}

/** O item pertence à área? Itens comuns e os fixados na base da sidebar pertencem a todas. */
export function workspaceIncludes(workspace: Workspace, item: NavItem): boolean {
  if (!workspace.modules || item.placement === 'footer') return true;
  return COMMON_MODULES.includes(item.to) || workspace.modules.includes(item.to);
}

/** Filtra os módulos (já liberados por permissão) para a área, mantendo a ordem do menu. */
export function filterByWorkspace(modules: NavItem[], workspace: Workspace): NavItem[] {
  return modules.filter((item) => workspaceIncludes(workspace, item));
}

/**
 * Área a exibir para a rota atual: a escolhida, se contém o módulo aberto; senão a primeira que o
 * contém (ex.: abrir Financeiro pela busca estando em Operacional muda para Financeiro).
 */
export function resolveWorkspace(selected: WorkspaceId, pathname: string): Workspace {
  const current = getWorkspace(selected);
  const module = findActiveModule(pathname);
  if (!module || workspaceIncludes(current, module)) return current;
  return WORKSPACES.find((w) => workspaceIncludes(w, module)) ?? current;
}
